import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { getArticleBySlug, type ArticleSection } from '@/mocks/articles';

function renderSection(section: ArticleSection, idx: number) {
  switch (section.type) {
    case 'heading':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
        </section>
      );

    case 'paragraph':
      return (
        <section key={idx} className="mb-10">
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
        </section>
      );

    case 'table':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
          <div className="bg-background-100 rounded-xl p-6 border border-background-200/70 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-background-200/70">
                  {section.tableHeaders?.map((h, hi) => (
                    <th key={hi} className={`py-3 px-4 text-sm font-semibold whitespace-nowrap ${hi === 0 ? 'text-foreground-700' : hi === 1 ? 'text-primary-500 bg-primary-50/30 rounded-t-lg' : 'text-foreground-600'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.tableRows?.map((row, ri) => (
                  <tr key={ri} className="border-b border-background-200/50">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`py-3 px-4 text-sm whitespace-nowrap ${ci === 0 ? 'font-medium text-foreground-800' : ci === 1 ? 'font-semibold text-accent-700 bg-primary-50/20' : 'text-foreground-600'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );

    case 'list':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
          <ul className="space-y-2 list-disc pl-5">
            {section.listItems?.map((item, li) => (
              <li key={li} className="text-base text-foreground-700 leading-relaxed">
                <span dangerouslySetInnerHTML={{ __html: item.includes('—') ? `<strong>${item.split('—')[0]}</strong> — ${item.split('—').slice(1).join('—')}` : item }} />
              </li>
            ))}
          </ul>
        </section>
      );

    case 'cards':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {section.cardItems?.map((card, ci) => (
              <div key={ci} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 mb-3">
                  <i className={`${card.icon} text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{card.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case 'ordered-list':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
          <ol className="space-y-4 list-decimal pl-5">
            {section.orderedItems?.map((item, oi) => (
              <li key={oi} className="text-base text-foreground-700 leading-relaxed">
                <strong>{item}</strong>
              </li>
            ))}
          </ol>
        </section>
      );

    case 'steps':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, pi) => (
            <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
          ))}
          <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
            <div className="space-y-5">
              {section.stepItems?.map((step, si) => (
                <div key={si} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-700">
                    {step.number}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950 mb-1">{step.title}</p>
                    <p className="text-sm text-foreground-600">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'faq':
      return (
        <section key={idx} className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {section.heading}
            </h2>
          )}
          <div className="space-y-4">
            {section.faqItems?.map((faq, fi) => (
              <div key={fi} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{faq.q}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section key={idx} className="border-t border-background-200/70 pt-12 mb-10">
          <div className="bg-primary-50/50 rounded-2xl p-8 md:p-10 text-center border border-primary-100/50">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              {section.ctaText}
            </h2>
            {section.paragraphs?.map((p, pi) => (
              p ? <p key={pi} className="text-base text-foreground-600 leading-relaxed mb-6 max-w-lg mx-auto">{p}</p> : null
            ))}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {section.ctaLink && (
                <a
                  href={section.ctaLink}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {section.ctaLabel || 'Get Started'}
                </a>
              )}
              <Link
                to="/"
                className="w-full sm:w-auto px-8 py-3.5 border border-foreground-300 text-foreground-800 rounded-full text-base font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [isScrolled, setIsScrolled] = useState(false);
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!article) {
    return (
      <main className="bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been removed.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': article.schemaType,
    headline: article.schemaHeadline,
    description: article.schemaDescription,
    image: article.image,
    author: { '@type': 'Organization', name: 'AllChinaBuy.works' },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://allchinabuy.works/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'AllChinaBuy.works',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg',
      },
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main className="bg-background-50">
        <Navbar isScrolled={isScrolled} />

        <article>
          <header className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-10 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors mb-8 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
                {article.tag} · {article.date}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>Updated: {article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </header>

          <div className="relative h-64 md:h-96 mx-6 md:mx-10 rounded-2xl overflow-hidden mb-16">
            <img
              src={article.image}
              alt={article.imageAlt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          <div className="max-w-3xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
            <div className="prose prose-lg max-w-none">
              {article.sections.map((section, idx) => renderSection(section, idx))}
            </div>

            <div className="mt-16 pt-10 border-t border-background-200/70 text-center">
              <p className="text-sm text-foreground-500 mb-6">Explore more AllChinaBuySpreadsheet resources:</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-5 py-2.5 bg-background-100 border border-background-200/70 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-accent-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-4-line mr-1.5"></i>
                  Homepage
                </Link>
                <Link
                  to="/article/allchinabuyspreadsheet-complete-guide-2026"
                  className="px-5 py-2.5 bg-background-100 border border-background-200/70 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-accent-200 transition-colors whitespace-nowrap"
                >
                  Complete Guide
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Access Spreadsheet
                </a>
              </div>
            </div>
          </div>
        </article>

        <footer className="bg-foreground-950 text-background-50 px-6 md:px-10 py-10">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="font-heading text-xl font-bold tracking-tight">
              AllChinaBuy<span className="text-primary-500">.works</span>
            </Link>
            <p className="text-xs text-background-50/40">
              &copy; {new Date().getFullYear()} AllChinaBuy.works. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </>
  );
}